import {IVendor} from "./Search";
import {IActionResponse} from "../dal/BaseDAL";

export interface IProductBundleRef {
    id?: string;
    name?: string;
    partNumber?: string;
    active?: boolean;
    imageUrl?: string;
}

export interface IProductBundle extends IProductBundleRef {
    description?: string;
    category?: string;
    segment?: string;
    details?: IStorefrontProductBundleDetails;
}

export interface IProductBundleItem {
    id?: string;
    productId: string;
    // When set, this bundle item is a saved search (at most one per bundle). It is always required,
    // has no optionality controls, and renders pinned at the top of the item list, above groups.
    savedSearchId?: string;
    bundleId?: string;
    catalog?: string;
    sortOrder?: number;
    details?: IProductBundleItemDetails;
    vendors?: IVendor[];
    sourceItem?: IProductBundleItemSourceProductRef;
}

export interface IAddProductToBundleResponse extends IActionResponse {
    item?: IProductBundleItem;
}

export interface IProductBundleItemSourceProductRef {
    name?: string;
    manufacturerPartNumber?: string;
    imageUrl?: string;
    segment?: string;
    category?: string;
}

export interface IProductBundleItemDetails {
    quantity?: number;
    price?: number;
    markup?: number;
    listDiscount?: number;
    isTaxable?: boolean;
    taxRate?: number;
    taxCode?: string;
    source?: string;
    priceLevelPartNumber?: string;
    discountedCost?: number;
    overrideCost?: number;
    optionality?: string;
    changeQuantity?: boolean;
    isDefault?: boolean;
}
export interface IStorefrontProductBundleDetails {
    defaultListDiscount?: number;
    defaultSource?: string;
    defaultTaxable?: boolean;
    defaultTaxCode?: string;
    defaultTaxRate?: number;
    hideLineItemDetails?: boolean;
    descriptionSourceItems?: string[];
    includeInAllStorefronts?: boolean;
    alwaysAvailable?: boolean;
    minimumPrice?: number;
    includeProductQuestions?: boolean;
    pricing?: IStorefrontProductBundlePricing[];
    storefronts?: string[];
    productGroups?: IStorefrontProductBundleGroup[];
}

export interface IStorefrontProductBundlePricing {
    storefront: string;
    markupType?: string;
    markup?: number;
    minimumPrice?: number;
}

export interface IStorefrontProductBundleGroup {
    id?: string;
    name?: string;
    products: string[];
}

export function calculateBundleItemCost(vendors: IVendor[], source?: string, discountedCost?: number, overrideCost?: number) {
    if (overrideCost != undefined) return overrideCost;
    if (discountedCost != undefined) return discountedCost;
    if (source === 'LowestPrice') {
        const lowestCost = vendors.reduce((prev, current) => {
            return (prev.cost == null || (current.cost != null && prev.cost < current.cost)) ? prev : current;
        });
        return lowestCost.cost;
    } else if (source === 'LowestInStock' || source === undefined) {
        let lowestCost = 0;
        for (const vendor of vendors) {
            if (vendor.inStock || (vendor.onHand != null && vendor.onHand > 0)) {
                if (vendor.cost != null && (lowestCost === 0 || vendor.cost < lowestCost)) {
                    lowestCost = vendor.cost;
                }
            }
        }
        return lowestCost;
    }
    const vendor = vendors.find((v) => v.vendorName === source);
    if (vendor) {
        if (vendor.cost !== undefined) {
            return vendor.cost;
        }
    }
    return 0;
}


export function calculateBundleItemPrice(item: IProductBundleItem, defaultMarkup?: number, defaultListDiscount?: number) : string | undefined {
    if (item.details?.price != undefined) return item.details.price.toFixed(2);
    const vendors = item.vendors;
    if ((item.details?.markup != undefined || defaultMarkup != undefined) && vendors) {
        const markup = (item.details?.markup ?? defaultMarkup ?? 0) + 100;
        return (Math.round((calculateBundleItemCost(vendors, item.details?.source, item.details?.discountedCost, item.details?.overrideCost) ?? 0) * markup) / 100).toFixed(2);
    }
    return undefined;
}

/** What one unit of a live bundle item is worth on the bundle picker (the band) and its peek flyout. */
export interface IBundleItemFigure {
    /** Cheapest known unit figure; equal to `high` when the line carries a single fixed number. */
    low: number;
    /** Dearest known unit figure. */
    high: number;
    /** The figure is the item's hand-entered Override Price, not a cost. */
    isOverridePrice: boolean;
}

/**
 * The per-unit figure behind a bundle item on the picker, in the order an override overrides:
 * its hand-entered Override Price, else its cost override, else a quantity price break, else the
 * cheapest and dearest vendor cost (in-stock vendors preferred).
 *
 * The Override Price wins outright, matching how the server prices the same item: Details.Price sets
 * the markup type to OVERRIDE, which beats the item markup, the bundle default and the workspace
 * markup alike. A rep who typed a price on a line meant that line to be worth that, whether or not a
 * vendor happens to carry it, so the picker must not quietly show the vendor's cost instead.
 *
 * That makes the figure a price on hand-priced lines and a cost on the rest, which is as far as the
 * picker can get: it has no markup to price the vendor-sourced lines with. Callers say so wherever
 * they show the number. Returns undefined when nothing puts a number on the line.
 */
export function calculateBundleItemFigure(item: IProductBundleItem): IBundleItemFigure | undefined {
    const details = item.details;
    if (details?.price) return {low: details.price, high: details.price, isOverridePrice: true};
    if (details?.overrideCost) return {low: details.overrideCost, high: details.overrideCost, isOverridePrice: false};
    if (details?.discountedCost) return {low: details.discountedCost, high: details.discountedCost, isOverridePrice: false};
    const priced = (item.vendors ?? []).filter((v) => (v.cost ?? 0) > 0);
    const inStock = priced.filter((v) => v.inStock);
    const pool = inStock.length > 0 ? inStock : priced;
    if (pool.length > 0) {
        const costs = pool.map((v) => v.cost as number);
        return {low: Math.min(...costs), high: Math.max(...costs), isOverridePrice: false};
    }
    return undefined;
}
